/**
 * unlockArchive.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";
import * as React from "react";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimeValidationError } from "@mui/x-date-pickers/models";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Button, CircularProgress, TextField } from "@mui/material";

import { AuthContext } from "../auth/auth.context";

import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { archiveServiceFactory } from "../services/archive.service";

interface Props {
  pemPrivateKey: string,
  unlocked: boolean,
  setUnlocked(unlocked: boolean): void,
  setPemPrivateKey(key: string): void,
}

export function UnlockArchiveComponent(props: Props): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const [startDate, setStartDate] = React.useState<DateTime>(DateTime.now().startOf("day").minus({months: 6}));
  const [endDate, setEndDate] = React.useState<DateTime>(DateTime.now().endOf("day"));
  const [startDateErr, setStartDateErr] = React.useState<string>("");
  const [endDateErr, setEndDateErr] = React.useState<string>("");
  const [sending, setSending] = React.useState<boolean>(false);
  const [errMsg, setErrMsg] = React.useState<string>("");

  React.useEffect(function () {
    if (!authContext.membership) { return; }
    if (props.unlocked) { return; }
    archiveServiceFactory().checkUnlocked(authContext.membership.accountID).then(range => {
      console.log("range:", range);
      let unlocked = false;
      if (range[0]) { setStartDate(range[0]); unlocked = true; }
      if (range[1]) { setEndDate(range[1]); unlocked = true; }
      if (unlocked) { props.setUnlocked(true); }
    });
  }, [authContext.membership]);

  function updatePrivateKey(event: React.SyntheticEvent) {
    props.setPemPrivateKey((event.target as HTMLInputElement).value);
  }

  function onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "startDate") {
      if (!startDate || startDate.invalidReason) {
        setStartDateErr("Please, set a starting date/time");
      } else if (endDate && endDate < startDate) {
        setStartDateErr("Starting date/time is after the ending date/time");
      } else {
        setStartDateErr("");
      }
    } else if (propName === "endDate") {
      if (!endDate || endDate.invalidReason) {
        setEndDateErr("Please, set an ending date/time");
      } else if (startDate && endDate < startDate) {
        setEndDateErr("Ending date/time is before the starting date/time");
      } else {
        setEndDateErr("");
      }
    }
  }
  function onDateError(error: DateTimeValidationError | null, propName: string) {
    if (propName === "startDate") {
      switch (error) {
        case "maxDate":
          setStartDateErr("Starting date/time must be in the past");
          break;
        case null:
          setStartDateErr("");
          break;
        default:
          setStartDateErr("Please, set a starting date/time");
          break;
      }
    } else if (propName === "endDate") {
      switch (error) {
        case "maxDate":
          setEndDateErr("Ending date/time must be in the past");
          break;
        case null:
          setEndDateErr("");
          break;
        default:
          setEndDateErr("Please, set an ending date/time");
          break;
      }
    }
  }

  async function unlock() {
    try {
      if (sending) { return; }
      let haveError = false;
      if (!startDate || startDate.invalidReason) {
        setStartDateErr("Please, set a starting date/time");
        haveError = true;
      } else if (endDate && endDate < startDate) {
        setStartDateErr("Starting date/time is after the ending date/time");
        haveError = true;
      } else {
        setStartDateErr("");
      }
      if (!endDate || endDate.invalidReason) {
        setEndDateErr("Please, set an ending date/time");
        haveError = true;
      } else if (startDate && endDate < startDate) {
        setEndDateErr("Ending date/time is before the starting date/time");
        haveError = true;
      } else {
        setEndDateErr("");
      }
      if (haveError) { return; }
      if (!authContext.membership) { return; }
      setSending(true);
      await archiveServiceFactory().unlockArchive(
        authContext.membership.accountID, startDate, endDate, props.pemPrivateKey);

      props.setUnlocked(true);
    } catch (err) {
      console.error("Error unlocking:", err);
      setErrMsg((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function relock() {
    try {
      if (!authContext.membership) { return; }
      if (sending) { return; }
      setSending(true);
      await archiveServiceFactory().lockArchive(
        authContext.membership.accountID);
      props.setUnlocked(false);
    } catch (err) {
      console.error("Error locking archive:", err);
      setErrMsg((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  /* 
  renderInput={(props: TextFieldProps) =>
              <TextField
                size="small"
                name="startDate"
                color="secondary"
                type="datetime-local"
                error={!!startDateErr}
                helperText={startDateErr}
                onBlur={onBlur}
                {...props}
              />}
  renderInput={(props: TextFieldProps) =>
              <TextField
                size="small"
                name="endDate"
                color="secondary"
                type="datetime-local"
                error={!!endDateErr}
                helperText={endDateErr}
                onBlur={onBlur}
                {...props}
              />}
    inputFormat="LL/dd/yyyy"
  */
  return <div className="unlock-archive-container">
    <h2>Unlock Archive</h2>
    <form className="form-wrapper">
      <div className="date-range-row">
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DatePicker
            label="Start Date"
            name="startDate"
            value={startDate}
            onChange={(newValue: DateTime | null) => {
              newValue && setStartDate(newValue);
            }}
            onError={e => onDateError(e, "startDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: startDateErr,
                error: !!startDateErr,
                onBlur: onBlur
              }
            }}
          />
          <DatePicker
            label="End Date"
            name="endDate"
            value={endDate}
            onChange={(newValue: DateTime | null) => {
              newValue && setEndDate(newValue);
            }}
            onError={e => onDateError(e, "endDate")}
            slotProps={{
              textField: {
                size: "small",
                helperText: endDateErr,
                error: !!endDateErr,
                onBlur: onBlur
              }
            }}
          />
        </LocalizationProvider>
      </div>
      <div className="form-row">
        <TextField className="privatekey-input" id="desc" name="desc" fullWidth multiline
          variant="outlined" label="Private Key" color="secondary" size="small" minRows={4} maxRows={10}
          value={props.pemPrivateKey} onChange={updatePrivateKey}  />
      </div>
      <div className="button-row">
        {sending && <CircularProgress size={25} />}
        {props.unlocked && <Button variant="contained" color="secondary"
          disabled={sending}
          onClick={relock}>Re-lock</Button>}
        <Button variant="contained" disabled={sending} onClick={unlock}>Unlock</Button>
      </div>
    </form>
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") } />
  </div>;
}
